import { GeoWorker } from '@workers';
// src/services/maps/tileCache.js
// Offline tile caching management.
// Coordinates between the app, the SW CacheStorage, and TileRegistry IDB.
// Called when the user is online to seed the cache for their area.

import { TileRegistry }      from '@services/storage/idb';
import { sendToSW }          from '@services/storage/sw';
import { ZOOM_THRESHOLDS }   from './config.js';

// ── Tile URL generation ───────────────────────────────────────────
// Convert a lat/lon + radius into tile URLs at the cache zoom levels.
export function getTileUrlsForBounds(bounds, zooms) {
  const urls = [];
  const levels = zooms ?? [
    ZOOM_THRESHOLDS.TILE_CACHE_MIN,
    ZOOM_THRESHOLDS.TILE_CACHE_MIN + 1,
    ZOOM_THRESHOLDS.TILE_CACHE_MIN + 2,
    ZOOM_THRESHOLDS.TILE_CACHE_MAX,
  ];

  for (const z of levels) {
    const { x: x0, y: y0 } = _latLonToTile(bounds.s, bounds.w, z);
    const { x: x1, y: y1 } = _latLonToTile(bounds.n, bounds.e, z);
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) {
      for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) {
        urls.push(`https://tile.openstreetmap.org/${z}/${x}/${y}.png`);
      }
    }
  }
  return [...new Set(urls)];  // deduplicate
}

export function getTileUrlsForRadius(lat, lon, radiusMiles = 8) {
  const deg = radiusMiles / 69;
  return getTileUrlsForBounds({
    n: lat + deg, s: lat - deg,
    e: lon + deg, w: lon - deg,
  });
}

// ── Prefetch tiles ────────────────────────────────────────────────
// Sends tile URLs to the SW for background caching.
// Also registers them in IDB TileRegistry for tracking.
export async function prefetchAreaTiles(lat, lon, radiusMiles = 8) {
  // Generate tile URLs in geo worker — can produce 1000+ URLs for large areas
  const deg    = radiusMiles / 69;
  const bounds = { n: lat + deg, s: lat - deg, e: lon + deg, w: lon - deg };
  const urls   = await GeoWorker.generateTileUrls(bounds).catch(() => getTileUrlsForRadius(lat, lon, radiusMiles));
  sendToSW({ type: 'PREFETCH_TILES', urls });
  await Promise.all(urls.map((url) => TileRegistry.register(url)));
  return urls.length;
}

// Prefetch from a specific Leaflet map bounds object
export async function prefetchViewportTiles(leafletBounds) {
  const bounds = {
    n: leafletBounds.getNorth(),
    s: leafletBounds.getSouth(),
    e: leafletBounds.getEast(),
    w: leafletBounds.getWest(),
  };
  const urls = getTileUrlsForBounds(bounds);
  sendToSW({ type: 'PREFETCH_TILES', urls });
  await Promise.all(urls.map((url) => TileRegistry.register(url)));
  return urls.length;
}

// ── Cache health check ────────────────────────────────────────────
export async function getTileCacheStats() {
  const registeredCount = await TileRegistry.count();
  // Ask SW for its actual CacheStorage count
  const swCount = await new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 1000);
    const handler = (e) => {
      if (e.data?.type === 'TILE_CACHE_STATS') {
        clearTimeout(timeout);
        navigator.serviceWorker.removeEventListener('message', handler);
        resolve(e.data.count);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    sendToSW({ type: 'GET_TILE_CACHE_STATS' });
  });

  return {
    registered:  registeredCount,
    swCached:    swCount,
    coverageKm2: _estimateCoverage(registeredCount),
  };
}

// Rough coverage estimate: OSM z10 tile ≈ 40km²,  z13 ≈ 0.6km²
function _estimateCoverage(tileCount) {
  return Math.round(tileCount * 2.5);  // rough average across zoom levels
}

// ── Trim tile cache by byte budget ─────────────────────────────────
// Called by useMemoryMonitor when the tile count approaches the limit.
// Removes the least-recently-accessed tiles until we're under budgetBytes.
// The SW maintains sw-accessed-at headers — we sort by those.
//
// Sends TRIM_TILES message to SW which evicts LRU entries.
// Returns estimated bytes freed.
export async function trimByBytes(budgetBytes = 40 * 1024 * 1024) {
  const stats = await getTileCacheStats();
  // Rough estimate: each tile ~15KB average across zoom levels
  const estimatedBytes = (stats.registered ?? 0) * 15_000;
  if (estimatedBytes <= budgetBytes) return 0;

  const toEvict = Math.ceil((estimatedBytes - budgetBytes) / 15_000);
  // Tell SW to trim LRU tiles
  sendToSW({ type: 'TRIM_TILES', targetCount: (stats.registered ?? 0) - toEvict });
  // Update IDB registry — evict by access time (oldest first)
  const evicted = await TileRegistry.evictLRU(toEvict);
  return evicted * 15_000;
}

// ── Tile coverage manifest for a zone ──────────────────────────────
// Written to /public_snapshots/tile_manifest_{zone} by the snapshot CF.
// Lets the app know which tiles are pre-cached server-side for a zone.
export async function readTileManifest(zone) {
  try {
    const url  = `/snapshots/tile_manifest_${zone.toLowerCase().replace(/\s+/g, '_')}.json`;
    const resp = await fetch(url);
    if (!resp.ok) return null;
    return resp.json();
  } catch { return null; }
}

// ── Evict stale tiles ─────────────────────────────────────────────
// Removes tile registry entries older than maxAgeMs.
// The SW handles actual CacheStorage eviction via LRU.
export async function evictOldTiles(maxAgeMs = 30 * 24 * 60 * 60_000) {
  const evicted = await TileRegistry.evictOld(maxAgeMs);
  return evicted;
}

// ── Helpers ───────────────────────────────────────────────────────
function _latLonToTile(lat, lon, z) {
  const n = Math.pow(2, z);
  const x = Math.floor((lon + 180) / 360 * n);
  const latR = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2 * n);
  return { x: Math.max(0, x), y: Math.max(0, y) };
}
